import { useEffect, useMemo, useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { api } from "@/lib/api";
import { Wifi, Car, Utensils, Flame, Wind, Coffee, Mountain } from "lucide-react";

const AMENITY_ICONS = {
    "Vlastní kuchyně": Utensils,
    "Vlastní koupelna": Wifi,
    "Relax zóna s křesly": Mountain,
    "Wi-Fi zdarma": Wifi,
    "Posezení s grilem": Flame,
    "Klimatizace": Wind,
    "Hlídané parkoviště 150 m": Car,
};

function dateRangeToISO(range) {
    if (!range?.from) return null;
    const fmt = (d) => d.toISOString().slice(0, 10);
    return { start: fmt(range.from), end: fmt(range.to || range.from) };
}

function eachDate(startISO, endISO) {
    const out = [];
    const s = new Date(startISO);
    const e = new Date(endISO);
    let cur = new Date(s);
    while (cur < e) {
        out.push(new Date(cur));
        cur.setDate(cur.getDate() + 1);
    }
    return out;
}

export default function Accommodation({ onBook }) {
    const [acc, setAcc] = useState(null);
    const [bookings, setBookings] = useState([]);
    const [range, setRange] = useState();
    const [activeImg, setActiveImg] = useState(0);

    useEffect(() => {
        api.get("/accommodation").then((r) => setAcc(r.data)).catch(() => setAcc(null));
        api.get("/availability").then((r) => setBookings(r.data)).catch(() => setBookings([]));
    }, []);

    const bookedDates = useMemo(() => {
        const all = [];
        for (const b of bookings) {
            try {
                all.push(...eachDate(b.start_date, b.end_date));
            } catch { /* ignore */ }
        }
        return all;
    }, [bookings]);

    const nights = useMemo(() => {
        if (!range?.from || !range?.to) return 0;
        const ms = range.to - range.from;
        return Math.max(1, Math.round(ms / (1000 * 60 * 60 * 24)));
    }, [range]);

    if (!acc) {
        return (
            <section id="ubytovani" className="py-24 px-6 md:px-12">
                <p className="text-ink2">Načítám…</p>
            </section>
        );
    }

    const total = nights * acc.price_per_night;

    return (
        <section id="ubytovani" data-testid="accommodation-section" className="py-24 md:py-36 px-6 md:px-12 bg-cream2">
            <div className="mx-auto max-w-[1500px]">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 items-end">
                    <div className="lg:col-span-8">
                        <p className="overline mb-6">Ubytování · Apartmán · Vinice</p>
                        <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-ink">
                            <span className="font-serif-italic text-burgundy">Pobyt</span> mezi vinicemi
                        </h2>
                    </div>
                    <div className="lg:col-span-4 lg:text-right">
                        <div className="font-serif text-4xl md:text-5xl text-ink">
                            od {acc.price_per_night} Kč
                        </div>
                        <div className="text-xs uppercase tracking-[0.2em] text-ink2 mt-2">za osobu/noc · 2 hosté + přistýlka pro další 2 (celkem až 4)</div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                    {/* gallery */}
                    <div className="lg:col-span-7">
                        <div className="image-hover overflow-hidden h-[460px] md:h-[600px] mb-4">
                            <img
                                src={acc.images[activeImg]}
                                alt={acc.name}
                                className="w-full h-full object-cover"
                                key={activeImg}
                            />
                        </div>
                        <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                            {acc.images.map((src, i) => (
                                <button
                                    key={src}
                                    data-testid={`accommodation-thumb-${i}`}
                                    onClick={() => setActiveImg(i)}
                                    className={`aspect-square overflow-hidden image-hover transition-all ${activeImg === i ? "ring-2 ring-burgundy" : "opacity-70 hover:opacity-100"}`}
                                >
                                    <img src={src} alt="" className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>

                        <div className="mt-12">
                            <h3 className="font-serif text-3xl md:text-4xl text-ink mb-5">{acc.name}</h3>
                            <p className="text-base md:text-lg text-ink2 leading-relaxed max-w-2xl">{acc.description}</p>

                            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-5 mt-10 pt-10 border-t border-ink/15">
                                {acc.amenities.map((a) => {
                                    const Icon = AMENITY_ICONS[a] || Mountain;
                                    return (
                                        <div key={a} className="flex items-center gap-3 text-sm text-ink">
                                            <Icon size={18} className="text-burgundy shrink-0" />
                                            <span>{a}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* sticky reservation */}
                    <div className="lg:col-span-5">
                        <div className="lg:sticky lg:top-28 bg-cream border border-ink/15 p-6 md:p-8">
                            <p className="overline mb-4">Rezervace · Dostupnost</p>
                            <h3 className="font-serif text-3xl md:text-4xl text-ink mb-6 leading-tight">
                                Vyberte termín
                            </h3>
                            <div className="flex justify-center" data-testid="availability-calendar">
                                <Calendar
                                    mode="range"
                                    selected={range}
                                    onSelect={setRange}
                                    numberOfMonths={1}
                                    disabled={[{ before: new Date() }, ...bookedDates]}
                                    modifiers={{ booked: bookedDates }}
                                    modifiersClassNames={{ booked: "booked-day" }}
                                    weekStartsOn={1}
                                />
                            </div>

                            <div className="mt-6 pt-6 border-t border-ink/15 space-y-3 text-sm text-ink">
                                <div className="flex justify-between">
                                    <span className="text-ink2">Cena za osobu/noc</span>
                                    <span>{acc.price_per_night} Kč</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-ink2">Počet hostů</span>
                                    <span data-testid="guests-count">2</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-ink2">Počet nocí</span>
                                    <span data-testid="nights-count">{nights}</span>
                                </div>
                                <div className="flex justify-between text-base font-semibold pt-3 border-t border-ink/15">
                                    <span>Celkem (2 hosté)</span>
                                    <span data-testid="total-price">{total * 2} Kč</span>
                                </div>
                                <p className="text-[11px] text-ink2 leading-relaxed pt-1">Přistýlka pro další 2 osoby na vyžádání (celková kapacita 4 hosté). Konečnou cenu potvrdíme po rezervaci.</p>
                            </div>

                            <button
                                data-testid="accommodation-book-button"
                                disabled={!range?.from || !range?.to}
                                onClick={() => {
                                    const r = dateRangeToISO(range);
                                    onBook(`${r.start} – ${r.end} (${nights} nocí)`);
                                }}
                                className="btn-burgundy w-full mt-6"
                            >
                                Rezervovat pobyt
                            </button>

                            <p className="mt-4 text-xs text-ink2 leading-relaxed">
                                Po odeslání žádosti vás kontaktujeme a potvrdíme dostupnost. Kalendář je
                                synchronizován přes <span className="text-burgundy">iCal</span>.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
